import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { AppleLogoIcon, GoogleChromeLogoIcon, GooglePlayLogoIcon } from '@phosphor-icons/react';
import { v1App } from '@/infrastructure/clients/BackendClient';
import { toResult } from '@/infrastructure/api/v1/client';
import { getWebxrBaseUrl } from '@/lib/productHosts';
import { decideLanding, detectBrowser, readShareResolve, type LandingDecision, type ShareResolve } from '@/lib/shareLanding';
import LureoButton from '@/common/LureoButton';
import { cn } from '@/utils/utils';
import { TextButton, TextLink } from '@/components/ui/text-button';
import { QrCode } from '@/components/ui/qr-code';
import { lureoButtonClass } from '@/common/lureoButtonClass';
import { PageFrame } from '@/components/ui/page-frame';
import { ThemedLogo } from '@/modules/theme';
import lureoBetaLogo from '@/assets/Lureo_Beta_2.png';

const IOS_STORE_URL = 'https://apps.apple.com/app/lureo/id6754248929';
const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.imvizar.lureo';

// Off until a test on an iPhone shows a link on this page can launch the App Clip with this story
const OPEN_IN_APP_CLIP_URL: string | null = null;

// The resolver needs the app key alone; the slug travels in a header, out of URLs and access logs
async function resolveSlug(slug: string): Promise<ShareResolve> {
  return readShareResolve(await toResult(v1App.GET('/api/v1/share-links/resolve', {
    params: { header: { 'X-Lureo-Share-Link': slug } },
  })));
}

/**
 * Public landing for share links (`/s/:slug`, share links 3.1). Resolves the slug and obeys the
 * browser route for this device (src/lib/shareLanding.ts): the viewer on Android, the App Clip and
 * App Store on an iPhone, a QR code on a desktop, the store or an explanation where nothing runs.
 * Deliberately outside ProtectedRoute: a stranger scanning a poster has no account.
 */
export default function ShareLanding() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { search } = useLocation();
  const [resolved, setResolved] = useState<ShareResolve | null>(null);
  const [prevSlug, setPrevSlug] = useState(slug);
  if (prevSlug !== slug) {
    setPrevSlug(slug);
    setResolved(null);
  }

  useEffect(() => {
    let cancelled = false;
    resolveSlug(slug).then((result) => {
      if (!cancelled) setResolved(result);
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const browser = useMemo(() => detectBrowser(navigator.userAgent, navigator.maxTouchPoints), []);
  const shareUrl = `${window.location.origin}/s/${encodeURIComponent(slug)}${search}`;
  const decision: LandingDecision | null = useMemo(() => {
    if (resolved?.outcome !== 'config') return null;
    return decideLanding({
      config: resolved.config,
      browser,
      webxrBase: getWebxrBaseUrl(),
      search,
      shareUrl,
      iosStoreUrl: IOS_STORE_URL,
      androidStoreUrl: ANDROID_STORE_URL,
    });
  }, [resolved, browser, search, shareUrl]);

  useEffect(() => {
    if (decision?.kind === 'hop') window.location.replace(decision.url);
  }, [decision]);

  if (!resolved || decision?.kind === 'hop') {
    return (
      <Shell>
        <p className="text-lureo-14 text-lureo-on-emphasis-subtle leading-5">Opening your story…</p>
        {decision?.kind === 'hop' && (
          <TextLink tone="hero" href={decision.url}>
            Tap here if nothing happens
          </TextLink>
        )}
      </Shell>
    );
  }

  if (resolved.outcome !== 'config') {
    const copy = {
      dead: {
        title: "This link isn't active right now",
        body: 'Its creator may have taken the story offline for a while, or turned the link off. If you expected it to work, ask whoever shared it.',
      },
      unknown: { title: "This link doesn't lead anywhere", body: 'Check the address you were given, or ask whoever shared it for a new link.' },
      error: { title: "We couldn't open this link", body: 'Something went wrong on our side. Check your connection and try again.' },
    }[resolved.outcome];
    return (
      <Shell>
        <Title>{copy.title}</Title>
        <Body>{copy.body}</Body>
        {resolved.outcome === 'error' && (
          <Actions>
            <LureoButton variant="secondary" suppressTooltip onClick={() => window.location.reload()} className={BUTTON_SIZE}>
              Try again
            </LureoButton>
          </Actions>
        )}
      </Shell>
    );
  }

  if (!decision) {
    return (
      <Shell>
        <Title>A Lureo story</Title>
      </Shell>
    );
  }

  if (decision.kind === 'message') {
    return (
      <Shell>
        <Title>{decision.title}</Title>
        <Body>{decision.body}</Body>
      </Shell>
    );
  }

  if (decision.kind === 'pin') {
    return (
      <Shell>
        <Title>{decision.title}</Title>
        <Body>This story is for Spectacles. Enter this code on your Spectacles to open it.</Body>
        {decision.pin && (
          <p className="mt-8 font-lureo-font-mono text-lureo-36 font-semibold tracking-[0.3em] text-lureo-on-emphasis leading-10" aria-label="Spectacles code">
            {decision.pin}
          </p>
        )}
      </Shell>
    );
  }

  if (decision.kind === 'appClip') {
    return (
      <Shell>
        <Title>{decision.title}</Title>
        <Body>
          {decision.inSafari
            ? 'This story plays in the Lureo App Clip, with nothing to install, or in the Lureo app.'
            : 'Open this link in Safari to play it in the Lureo App Clip, with nothing to install, or get the Lureo app.'}
        </Body>
        <Actions>
          {OPEN_IN_APP_CLIP_URL && <a href={OPEN_IN_APP_CLIP_URL} className={PRIMARY_LINK}>Open in App Clip</a>}
          {!decision.inSafari && <a href={`x-safari-${shareUrl}`} className={OPEN_IN_APP_CLIP_URL ? SECONDARY_LINK : PRIMARY_LINK}><SafariGlyph className={BUTTON_ICON} />Open in Safari</a>}
          <a href={decision.storeUrl || IOS_STORE_URL} className={OPEN_IN_APP_CLIP_URL || !decision.inSafari ? SECONDARY_LINK : PRIMARY_LINK}><AppleLogoIcon weight="fill" className={BUTTON_ICON} />App Store</a>
          <CopyLink url={shareUrl} />
        </Actions>
      </Shell>
    );
  }

  if (decision.kind === 'openInChrome') {
    return (
      <Shell>
        <Title>{decision.title}</Title>
        <Body>This app's browser can't play the story. Open it in Chrome to play it.</Body>
        <Actions>
          <a href={decision.chromeUrl} className={PRIMARY_LINK}><GoogleChromeLogoIcon weight="fill" className={BUTTON_ICON} />Open in Chrome</a>
          {decision.storeUrl && <a href={decision.storeUrl} className={SECONDARY_LINK}><GooglePlayLogoIcon weight="fill" className={BUTTON_ICON} />Play Store</a>}
          <CopyLink url={shareUrl} />
        </Actions>
      </Shell>
    );
  }

  return (
    <Shell>
      <Title>{decision.title}</Title>
      <Body>Scan this code with your phone's camera to open the story.</Body>
      <Actions>
        <QrCode value={decision.link} size={200} />
        <a href={IOS_STORE_URL} className={PRIMARY_LINK}><AppleLogoIcon weight="fill" className={BUTTON_ICON} />App Store</a>
        <a href={ANDROID_STORE_URL} className={PRIMARY_LINK}><GooglePlayLogoIcon weight="fill" className={BUTTON_ICON} />Play Store</a>
        <CopyLink url={decision.link} />
      </Actions>
    </Shell>
  );
}

// The viewer's 264 by 48 buttons: the first choice in the call to action's gradient, any after it see-through. The share
// screens are set in px (LPM-1926), as this app's rem is 14px and the viewer's 16
const BUTTON_SIZE = 'h-[48px] w-[264px] max-w-full justify-center rounded-lureo-radius-button-pill px-6 text-[15px] font-semibold'; // theme-exempt: the share screens' 15px label, in px like the viewer's
const PRIMARY_LINK = cn(lureoButtonClass('secondary'), BUTTON_SIZE);
const SECONDARY_LINK = cn(lureoButtonClass('on-emphasis'), BUTTON_SIZE);
const BUTTON_ICON = 'h-[18px] w-[18px] shrink-0';

// Safari's compass, which Phosphor lacks: its ring and needle on Phosphor's grid, filled like the other marks
function SafariGlyph({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 256 256" fill="currentColor" aria-hidden className={className}>
      <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Z" />
      <path d="M184,72 139.3,139.3 72,184 116.7,116.7Z" />
    </svg>
  );
}

function CopyLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <TextButton
      tone="emphasis-link"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          window.prompt('Copy this link', url);
        }
      }}
    >
      {copied ? 'Copied' : 'Copy link'}
    </TextButton>
  );
}

const TITLE = 'line-clamp-3 w-full text-pretty font-lureo-font-ui text-[28px] font-bold leading-[1.22] tracking-[-0.025em] text-lureo-on-emphasis'; // theme-exempt: the share screens' 28px title (LPM-1926), between the 24 and 30 steps

function Title({ children }: { children: React.ReactNode }) {
  return <h1 className={TITLE}>{children}</h1>; // ui-exempt: the viewer's and the App Clip's share title, at a size no title component draws
}

function Body({ children }: { children: React.ReactNode }) {
  return <p className="mx-auto mt-[12px] max-w-[320px] font-lureo-font-prose text-[15px] leading-[1.4] text-lureo-on-emphasis/72">{children}</p>; // theme-exempt: the share screens' 15px subtext, in px like the viewer's
}

function Actions({ children }: { children: React.ReactNode }) {
  return <div className="mt-[clamp(20px,4vh,32px)] flex w-full flex-col items-center gap-[20px]">{children}</div>; // theme-exempt: the share screens' 20px between buttons, in px like the viewer's
}

// The viewer's and the App Clip's share screens (LPM-1926): Lureo's own plum whatever the visitor's theme, the logo
// above the story, the call to action in its gradient
// Logo and title stand where the viewer's screens put them on every state: the draft pill's row kept, then its gaps
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <PageFrame layout="content" surface="own" className="brand-light bg-lureo-emphasis-strong">
      <div className="container flex flex-col items-center pb-24 text-center">
        <div aria-hidden className="h-[calc(max(24px,env(safe-area-inset-top))+36px)] shrink-0" />
        <ThemedLogo src={lureoBetaLogo} alt="Lureo" className="mt-[clamp(40px,14vh,120px)] h-auto w-[150px]" />
        <div className="mt-[clamp(32px,12vh,103px)] flex w-full max-w-[400px] flex-col items-center">{children}</div>
      </div>
    </PageFrame>
  );
}
